import { ReactElement } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTableContext } from './Table';
import { IconButton } from './IconButton';
import { NumberInput } from './Input';
import { SingleDropdown } from './Dropdown';

export type PaginationProps = Props;

interface Props {
  rowsPerPage?: RowsPerPage | undefined;
  setRowsPerPage?: ((rows: RowsPerPage) => void) | undefined;
  totalPages?: number | undefined;
  page: number;
  setPage: (page: number) => void;
  removePadding?: boolean | string | undefined;
}

export enum RowsPerPage {
  ten = 10,
  fifteen = 15,
  twenty = 20,
  twentyfive = 25,
}

const pageOptions: Array<{
  label: string;
  value: RowsPerPage;
}> = [
  {
    label: String(RowsPerPage.ten),
    value: RowsPerPage.ten,
  },
  {
    label: String(RowsPerPage.fifteen),
    value: RowsPerPage.fifteen,
  },
  {
    label: String(RowsPerPage.twenty),
    value: RowsPerPage.twenty,
  },
  {
    label: String(RowsPerPage.twentyfive),
    value: RowsPerPage.twentyfive,
  },
];

export const ContextualTablePagination = ({
  hideRowsPerPage,
}: {
  hideRowsPerPage?: boolean;
}) => {
  const { page, setPage, totalPages, rowsPerPage, setRowsPerPage } =
    useTableContext();

  return (
    <div className="border-t border-grey-500/40">
      <Pagination
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        rowsPerPage={hideRowsPerPage ? undefined : rowsPerPage}
        setRowsPerPage={hideRowsPerPage ? undefined : setRowsPerPage}
      />
    </div>
  );
};

export const TablePagination = (props: Props) => (
  <div className="border-t border-grey-500/40">
    <Pagination {...props} />
  </div>
);

const Pagination = ({
  rowsPerPage,
  setRowsPerPage,
  page,
  setPage,
  totalPages,
  removePadding,
}: Props): ReactElement => (
  <div
    className={`flex items-center justify-between ${removePadding ?? 'p-5'}`}
  >
    <div className="flex items-center space-x-3">
      {rowsPerPage && setRowsPerPage && (
        <>
          <span className="text-sm">Rows per page</span>
          <SingleDropdown
            options={pageOptions}
            onOptionSelect={(val) => setRowsPerPage(val)}
            selected={rowsPerPage}
          />
        </>
      )}
    </div>
    {totalPages !== undefined && (
      <div className="flex items-center space-x-3">
        <IconButton
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          Icon={ChevronLeftIcon}
        />
        {totalPages === 1 ? (
          <span className='text-sm'>{page}</span>
        ) : (
          <NumberInput
            className="w-11 !mb-0 !h-9 text-sm"
            inputClassname="text-center !h-9 leading-none"
            value={page}
            onChange={(e) =>
              setPage(
                e.target.value.length
                  ? Number(e.target.value) === 0
                    ? 1
                    : Number(e.target.value) > totalPages
                    ? totalPages
                    : Number(e.target.value)
                  : NaN
              )
            }
          />
        )}
        <span className='text-sm'>of</span>
        <span className='text-sm'>{totalPages}</span>
        <IconButton
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages}
          Icon={ChevronRightIcon}
        />
      </div>
    )}
  </div>
);

export default Pagination;
